import { useState, useEffect } from 'react';
import { ENTITY_MODAL_TYPES } from './entity-modal.constants';
import { useTranslation } from 'react-i18next';
import {
  FieldLabel,
  ModalInput,
  ModalTextarea,
  CustomSelect,
  Pill,
  Section,
  ProgressBar,
  TagList,
} from './EntityModalComponents';
import type { EntityFormProps } from './entity-modal.types';

const accent = ENTITY_MODAL_TYPES.objective.accent;

type Granularity = '' | 'jour' | 'semaine' | 'mois' | 'trimestre' | 'annee';

const GRANULARITY_BUTTONS: { key: Granularity; label: string }[] = [
  { key: 'jour', label: 'entity.goal_granularity_day' },
  { key: 'semaine', label: 'entity.goal_granularity_week' },
  { key: 'mois', label: 'entity.goal_granularity_month' },
  { key: 'trimestre', label: 'entity.goal_granularity_quarter' },
  { key: 'annee', label: 'entity.goal_granularity_year' },
];

const QUARTERS = ['Q1 2026', 'Q2 2026', 'Q3 2026', 'Q4 2026', 'Q1 2027', 'Q2 2027'];
const YEARS = ['2026', '2027', '2028', '2029', '2030'];

function inferGranularity(timeframe: string | undefined): Granularity {
  if (!timeframe) return '';
  if (/^Q\d\s\d{4}$/.test(timeframe)) return 'trimestre';
  if (/^\d{4}$/.test(timeframe)) return 'annee';
  if (/^\d{4}-\d{2}-\d{2}$/.test(timeframe)) return 'jour';
  if (/^\d{4}-W\d{2}$/.test(timeframe)) return 'semaine';
  if (/^\d{4}-\d{2}$/.test(timeframe)) return 'mois';
  return '';
}

interface ObjectiveFormProps extends EntityFormProps {
  relatedTaskCount?: { done: number; total: number };
}

export function ObjectiveForm({
  isEdit,
  formData,
  onChange,
  domains,
  tags,
  onAddTag,
  onRemoveTag,
  relatedTaskCount,
}: ObjectiveFormProps) {
  const { t } = useTranslation();
  const domainOptions = domains.map((d) => ({ value: d.id, label: d.name }));
  const [granularity, setGranularity] = useState<Granularity>(() =>
    inferGranularity(formData.timeframe)
  );

  useEffect(() => {
    if (isEdit && formData.timeframe) {
      setGranularity(inferGranularity(formData.timeframe));
    }
  }, [isEdit, formData.timeframe]);

  const handleGranularityChange = (g: Granularity) => {
    setGranularity(g);
    onChange('timeframe', '');
  };

  const granLabel =
    granularity === 'jour'
      ? t('entity.goal_target_day')
      : granularity === 'semaine'
        ? t('entity.goal_target_week')
        : granularity === 'mois'
          ? t('entity.goal_target_month')
          : granularity === 'trimestre'
            ? t('entity.goal_target_quarter')
            : granularity === 'annee'
              ? t('entity.goal_target_year')
              : '';

  return (
    <div className="flex flex-col gap-4">
      <input
        type="text"
        required
        placeholder={t('entity.goal_title_placeholder')}
        value={formData.title || ''}
        onChange={(e) => onChange('title', e.target.value)}
        className="w-full bg-transparent border-none text-lg font-semibold text-zinc-100 outline-none placeholder:text-zinc-600 tracking-tight"
      />

      <div className="flex gap-1.5">
        <Pill
          label={t('entity.goal_status_active')}
          color="#a855f7"
          active={formData.status === 'active'}
          onClick={() => onChange('status', 'active')}
        />
        <Pill
          label={t('entity.goal_status_accomplished')}
          color="#22c55e"
          active={formData.status === 'achieved'}
          onClick={() => onChange('status', 'achieved')}
        />
      </div>

      {isEdit && relatedTaskCount && relatedTaskCount.total > 0 && (
        <ProgressBar done={relatedTaskCount.done} total={relatedTaskCount.total} accent={accent} />
      )}

      <Section title={t('common.deadline')} defaultOpen accent={accent}>
        <div className="flex flex-col gap-3">
          <div>
            <FieldLabel>{t('entity.goal_granularity')}</FieldLabel>
            <div className="flex gap-1">
              {GRANULARITY_BUTTONS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => handleGranularityChange(g.key)}
                  className="flex-1 py-[7px] rounded-lg text-[11px] font-medium cursor-pointer transition-all"
                  style={{
                    border: `1px solid ${granularity === g.key ? accent : 'rgba(113,113,122,0.4)'}`,
                    backgroundColor: granularity === g.key ? `${accent}14` : 'transparent',
                    color: granularity === g.key ? accent : '#a1a1aa',
                  }}
                >
                  {t(g.label)}
                </button>
              ))}
            </div>
          </div>
          {granularity && (
            <div>
              <FieldLabel>{granLabel}</FieldLabel>
              {granularity === 'jour' && (
                <ModalInput
                  type="date"
                  value={formData.timeframe || ''}
                  onChange={(val) => onChange('timeframe', val)}
                />
              )}
              {granularity === 'semaine' && (
                <ModalInput
                  type="week"
                  value={formData.timeframe || ''}
                  onChange={(val) => onChange('timeframe', val)}
                />
              )}
              {granularity === 'mois' && (
                <ModalInput
                  type="month"
                  value={formData.timeframe || ''}
                  onChange={(val) => onChange('timeframe', val)}
                />
              )}
              {granularity === 'trimestre' && (
                <div className="grid grid-cols-3 gap-1.5">
                  {QUARTERS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => onChange('timeframe', q)}
                      className="py-2 rounded-lg text-[12px] font-medium cursor-pointer transition-all"
                      style={{
                        border: `1px solid ${formData.timeframe === q ? accent : 'rgba(113,113,122,0.35)'}`,
                        backgroundColor:
                          formData.timeframe === q ? `${accent}14` : 'rgba(9,9,11,0.4)',
                        color: formData.timeframe === q ? accent : '#a1a1aa',
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {granularity === 'annee' && (
                <div className="grid grid-cols-5 gap-1.5">
                  {YEARS.map((y) => (
                    <button
                      key={y}
                      type="button"
                      onClick={() => onChange('timeframe', y)}
                      className="py-2 rounded-lg text-[12px] font-medium cursor-pointer transition-all"
                      style={{
                        border: `1px solid ${formData.timeframe === y ? accent : 'rgba(113,113,122,0.35)'}`,
                        backgroundColor:
                          formData.timeframe === y ? `${accent}14` : 'rgba(9,9,11,0.4)',
                        color: formData.timeframe === y ? accent : '#a1a1aa',
                      }}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Section>

      <Section title={t('common.details')} defaultOpen accent={accent}>
        <div className="flex flex-col gap-3.5">
          <div>
            <FieldLabel>{t('common.domain')}</FieldLabel>
            <CustomSelect
              options={domainOptions}
              value={formData.domainId || ''}
              onChange={(val) => onChange('domainId', val)}
            />
          </div>
          <div>
            <FieldLabel>{t('common.description')}</FieldLabel>
            <ModalTextarea
              placeholder={t('entity.goal_description_placeholder')}
              value={formData.description || ''}
              onChange={(val) => onChange('description', val)}
            />
          </div>
        </div>
      </Section>

      <Section title="Tags" defaultOpen={false} accent={accent}>
        <TagList tags={tags} accent={accent} onAdd={onAddTag} onRemove={onRemoveTag} />
      </Section>

      <div>
        <FieldLabel>{t('common.budget')}</FieldLabel>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder={t('entity.goal_budget_placeholder')}
            value={formData.budget ?? ''}
            onChange={(e) =>
              onChange('budget', e.target.value === '' ? null : parseFloat(e.target.value))
            }
            className="w-full bg-zinc-900/40 border border-zinc-600/45 rounded-[10px] px-3 py-2.5 text-[13px] text-zinc-200 placeholder:text-zinc-600 focus:border-violet-500/50 focus:bg-zinc-900/60 outline-none transition"
          />
          <span className="text-sm text-zinc-500">€</span>
        </div>
      </div>
    </div>
  );
}
